/**
 * The 10-level config precedence (rgs-domain › config-precedence.md, `src/configs/configs.service.ts`).
 * A row is keyed by (operator, brand, game, currency); `*` brand = every brand of the operator, a brand
 * containing `*` is a pattern (`casino-*`), game 0 = every game, currency `*` = every currency. Each
 * bet column resolves on its own, most specific level first.
 */

export const CONFIG_LEVELS = [
  { key: 'game_currency', rank: 1, label: 'Brand + game + currency' },
  { key: 'game_all', rank: 2, label: 'Brand + game' },
  { key: 'brand_currency', rank: 3, label: 'Brand + currency' },
  { key: 'brand_all', rank: 4, label: 'Brand default' },
  { key: 'wildcard_game_currency', rank: 5, label: 'Pattern + game + currency' },
  { key: 'wildcard_game_all', rank: 6, label: 'Pattern + game' },
  { key: 'wildcard_currency', rank: 7, label: 'Pattern + currency' },
  { key: 'wildcard_all', rank: 8, label: 'Pattern default' },
  { key: 'operator_currency', rank: 9, label: 'Operator + currency' },
  { key: 'operator_all', rank: 10, label: 'Operator default' },
] as const;

export type ConfigLevelKey = (typeof CONFIG_LEVELS)[number]['key'];
export type ConfigLevel = (typeof CONFIG_LEVELS)[number];

export const levelByKey = (key: string): ConfigLevel | undefined => CONFIG_LEVELS.find((l) => l.key === key);

export type BrandScope = 'all' | 'pattern' | 'exact';

export const brandScope = (brand: string): BrandScope =>
  brand === '*' ? 'all' : brand.includes('*') ? 'pattern' : 'exact';

export interface ConfigScope {
  Brand: string;
  GameId: number;
  Currency: string;
}

/**
 * The level a row sits at. `null` means the resolver never reads it: a `*` (every brand) row applies
 * only with game 0, so a `*` row for one game is dead configuration.
 */
export function rowLevel({ Brand, GameId, Currency }: ConfigScope): ConfigLevel | null {
  const forGame = GameId !== 0;
  const forCurrency = Currency !== '*';
  const scope = brandScope(Brand);
  let key: ConfigLevelKey;
  if (scope === 'all') {
    if (forGame) return null;
    key = forCurrency ? 'operator_currency' : 'operator_all';
  } else if (scope === 'exact') {
    key = forGame ? (forCurrency ? 'game_currency' : 'game_all') : forCurrency ? 'brand_currency' : 'brand_all';
  } else {
    key = forGame
      ? forCurrency
        ? 'wildcard_game_currency'
        : 'wildcard_game_all'
      : forCurrency
        ? 'wildcard_currency'
        : 'wildcard_all';
  }
  return levelByKey(key) ?? null;
}

/** Settlement and Theme are brand-level: only rows for every game and every currency may carry them. */
export const isBrandLevelScope = ({ GameId, Currency }: Pick<ConfigScope, 'GameId' | 'Currency'>) =>
  GameId === 0 && Currency === '*';

/**
 * Does a brand pattern match a brand id? Mirrors the server's `brand LIKE REPLACE(pattern, '*', '%')`,
 * so `_` and `%` in a pattern act as wildcards too, and MySQL's default collation ignores case.
 */
export function brandMatches(pattern: string, brand: string): boolean {
  if (pattern === '*') return true;
  if (!pattern.includes('*')) return pattern.toLowerCase() === brand.toLowerCase();
  const regex = pattern
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(/[*%]/g, '.*')
    .replace(/_/g, '.');
  return new RegExp(`^${regex}$`, 'i').test(brand);
}

/** Plain-language scope pieces: `All brands`, `Brands matching casino-*`, `casino-uk`. */
export const describeBrand = (brand: string) => {
  const scope = brandScope(brand);
  return scope === 'all' ? 'All brands' : scope === 'pattern' ? `Brands matching ${brand}` : brand;
};

export const describeCurrency = (currency: string) => (currency === '*' ? 'All currencies' : currency);
